import React, { useMemo } from 'react'

import { Skeleton } from 'antd'
import { HStack, Icon, QuickTip, Text, VStack } from 'govwell-ui'
import { IconKey } from 'govwell-ui/components/Icon/util'
import isNil from 'lodash.isnil'
import { twMerge } from 'tailwind-merge'

import { formatNumber, NumberFormat } from 'src/utils/number'

export enum SummaryTileType {
  Number = 'Number',
  Percent = 'Percent',
  Hours = 'Hours',
}

type Props = {
  icon?: IconKey
  iconBgColor?: string
  iconColor?: string
  isLoading?: boolean
  label: string
  tooltip?: string
  type: SummaryTileType
  value?: number
}

const SummaryTile = ({
  icon = 'FaMessage',
  iconBgColor = 'bg-emerald-50',
  iconColor = 'text-emerald-600',
  isLoading,
  label,
  tooltip,
  type,
  value,
}: Props) => {
  const formattedValue = useMemo(() => {
    if (isNil(value)) {
      return ''
    }
    return formatNumber(value, { format: NumberFormat.Integer })
  }, [value])

  const suffix = useMemo(() => {
    switch (type) {
      case SummaryTileType.Percent:
        return '%'
      case SummaryTileType.Hours:
        return ' hrs'
      default:
        return ''
    }
  }, [type])

  const tileContent = (
    <HStack
      gap={4}
      align="center"
      className="h-full min-w-[200px] flex-1 rounded-lg border border-solid border-gray-200 bg-white p-5"
    >
      <div
        className={twMerge(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg',
          iconBgColor
        )}
      >
        <Icon icon={icon} className={twMerge('text-lg', iconColor)} />
      </div>
      <VStack gap={1}>
        <HStack gap={1} align="center">
          <Text.BodySmall className="text-gray-500 uppercase">{label}</Text.BodySmall>
          {tooltip && <QuickTip content={tooltip} />}
        </HStack>
        {isLoading ? (
          <Skeleton.Input active size="small" />
        ) : (
          <Text.BodyLarge className="font-bold">
            {formattedValue}
            {suffix}
          </Text.BodyLarge>
        )}
      </VStack>
    </HStack>
  )

  return tileContent
}

export default React.memo(SummaryTile)
